import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_API_KEY = 'surveyTemplate/industry/browse';

export interface IBrowseSurveyTemplatesQueryParams {
	industryName: string;
	pageSize?: number;
	pageToken?: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Browse survey templates for a specific industry with pagination.
 * Returns lightweight template cards without full survey definitions.
 *
 * @param params - Query parameters including industry name and pagination options
 * @returns React Query result with paged collection of survey template cards
 */
export function useBrowseSurveyTemplatesQuery(params: IBrowseSurveyTemplatesQueryParams) {
	const { industryName, pageSize = 25, pageToken, enabled = true, impersonationContext } = params;
	const userSession = useUserSession();

	return useQuery({
		queryFn: () => {
			const queryParams: Record<string, string> = {};
			if (pageSize) queryParams.pageSize = String(pageSize);
			if (pageToken) queryParams.pageToken = pageToken;

			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ISurveyTemplateCard>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams,
					urlPath: `surveytemplate/${industryName}/browse`,
				}),
				'GET'
			);
		},
		queryKey: [BASE_API_KEY, 'browse', industryName, pageSize, pageToken, impersonationContext?.account?.id],
		enabled: enabled && !!industryName,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
}

/**
 * Invalidates browse survey templates query cache.
 *
 * Call this when:
 * - Admin publishes/unpublishes a template
 * - Admin changes template's industry assignment
 * - Need to force refresh of template list
 *
 * @param industryName - If provided, only invalidates that industry's cache.
 *                       If omitted, invalidates all industries (use when template
 *                       industry changed or bulk operations performed).
 *
 * @example
 * // After publishing a Real Estate template
 * invalidateBrowseSurveyTemplatesQuery('RealEstate');
 *
 * @example
 * // After bulk import of templates across industries
 * invalidateBrowseSurveyTemplatesQuery(); // Refresh all
 */
export const invalidateBrowseSurveyTemplatesQuery = (industryName?: string) => {
	if (industryName) {
		reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, 'browse', industryName] });
	} else {
		reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, 'browse'] });
	}
};
